import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';
import { useField } from 'formik';

import * as graphql from 'generated/graphql';

import { WhiteButton } from 'packages/kakapo-ui-kit/components/buttons/WhiteButton';
import { ButtonSize } from 'packages/kakapo-ui-kit/types/Button';
import PrimaryButton from 'packages/kakapo-ui-kit/components/buttons/PrimaryButton';

import EvaluationSubmissionItemInput from './input/EvaluationSubmissionItemInput';
import ButtonSpinner from 'packages/kakapo-ui-kit/components/buttons/ButtonSpinner';
import EvaluationAnswerCard from './EvaluationAnswerCard';

function getFieldTypeKey(item: graphql.EvaluationSectionItem) {
	if (item.type === graphql.EvaluationSectionItemType.OpenQuestion) {
		return 'openQuestion';
	}

	if (item.type === graphql.EvaluationSectionItemType.Competency) {
		return 'competency';
	}

	if (item.type === graphql.EvaluationSectionItemType.Sentiment) {
		return 'sentiment';
	}

	if (item.type === graphql.EvaluationSectionItemType.Stars) {
		return 'stars';
	}

	if (item.type === graphql.EvaluationSectionItemType.ReviewGoals) {
		return 'reviewGoals';
	}

	return '';
}

function getFieldName(
	submission: graphql.EvaluationSubmission,
	sectionIndex: number,
	itemIndex: number,
) {
	const item = submission.evaluationForm.sections[sectionIndex].items[itemIndex];
	return `sections[${sectionIndex}].items[${itemIndex}].${getFieldTypeKey(item)}`;
}

export default function EvaluationAnswerComposer(props: {
	autosave?: boolean;
	section: number;
	item: number;
	metadata?: { goal: graphql.EvaluationSubmissionGoal };
	submission: graphql.EvaluationSubmission;
	result: graphql.EvaluationSubmissionResult;
	onSave?: () => void;
	isSaving: boolean;
	side: 'left' | 'right';
}) {
	const { t } = useTranslation();

	const item = props.submission.evaluationForm.sections[props.section].items[props.item];
	const name = getFieldName(props.submission, props.section, props.item);

	const [, { value, initialValue }, { setValue }] = useField(name);

	return (
		<EvaluationAnswerCard user={props.result.user} side={props.side} withAvatar>
			<EvaluationSubmissionItemInput
				submission={props.submission}
				item={item}
				metadata={props.metadata}
				name={name}
				initialValue={initialValue}
				party={props.result.party}
			/>

			{props.autosave ? null : (
				<Flex mt={2} justifyContent="flex-end" alignItems="center" style={{ columnGap: '5px' }}>
					<WhiteButton
						disabled={value === initialValue || props.isSaving}
						size={ButtonSize.Small}
						onClick={() => setValue(initialValue)}
					>
						{t('conversations.module.answer.discard')}
					</WhiteButton>
					<PrimaryButton
						size={ButtonSize.Small}
						onClick={props.onSave}
						disabled={props.isSaving || value === initialValue}
					>
						<ButtonSpinner isLoading={props.isSaving}>
							{t('conversations.module.answer.save')}
						</ButtonSpinner>
					</PrimaryButton>
				</Flex>
			)}
		</EvaluationAnswerCard>
	);
}
